import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { QrCode, ArrowLeft, LockKey } from "@phosphor-icons/react";
import { supabase } from "../lib/supabase";
import { useLang } from "../context/LangContext";

export default function ResetPassword() {
  const { t, lang, toggle } = useLang();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tokenHash = searchParams.get("token_hash");
  const r = t.auth;
  const [ready, setReady] = useState(!!tokenHash);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (tokenHash) return;
    let mounted = true;
    const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mounted) return;
      if (event === "PASSWORD_RECOVERY" && session) setReady(true);
    });
    supabase.auth.getSession().then(({ data, error }) => {
      if (!mounted) return;
      if (error) setError(error.message);
      else if (data.session) setReady(true);
      else setError(r.resetInvalid);
    });
    return () => { mounted = false; listener.subscription.unsubscribe(); };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) return setError(r.resetTooShort);
    if (password !== confirm) return setError(r.resetMismatch);
    setSaving(true);
    if (tokenHash) {
      const { error: vErr } = await supabase.auth.verifyOtp({ type: "recovery", token_hash: tokenHash });
      if (vErr) {
        setSaving(false);
        return setError(r.resetInvalid);
      }
    }
    const { error } = await supabase.auth.updateUser({ password });
    setSaving(false);
    if (error) return setError(error.message);
    setPassword("");
    setConfirm("");
    setDone(true);
    await supabase.auth.signOut();
    setTimeout(() => navigate("/login", { replace: true }), 1500);
  };

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="reset-password-page">
      <header className="border-b border-slate-200">
        <div className="mx-auto max-w-md px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center bg-[#0A0F1A] rounded-[4px]"><QrCode size={18} weight="bold" className="text-white" /></div>
            <span className="font-display font-bold text-[17px] tracking-tight">Certifico<span className="text-emerald-500">QR</span></span>
          </Link>
          <button onClick={toggle} className="font-mono-tech text-xs font-semibold border border-slate-300 rounded-[4px] px-2 py-1" data-testid="reset-lang-toggle">{lang.toUpperCase()}</button>
        </div>
      </header>

      <main className="flex-1 mx-auto w-full max-w-md px-6 py-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}>
          <div className="flex h-11 w-11 items-center justify-center rounded-[4px] bg-[#0A0F1A]"><LockKey size={22} className="text-emerald-500" /></div>
          <h1 className="mt-5 font-display font-bold text-3xl tracking-tight text-[#0F172A]">{r.resetTitle}</h1>

          {done ? (
            <p className="mt-6 text-emerald-600 font-medium" data-testid="reset-done">{r.resetDone}</p>
          ) : (
            <form onSubmit={submit} className="mt-8 space-y-5">
              {!ready && !error && <p className="text-sm text-slate-500" data-testid="reset-validating">{r.resetValidating}</p>}
              <div>
                <label className="block font-mono-tech text-xs uppercase tracking-wider text-slate-500 mb-2">{r.resetNew}</label>
                <input type="password" minLength={8} required autoComplete="new-password" disabled={!ready}
                  value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
                  className="w-full rounded-md border border-slate-300 px-4 py-3 text-[#0F172A] outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition disabled:bg-slate-50 disabled:text-slate-400" data-testid="reset-password-input" />
              </div>
              <div>
                <label className="block font-mono-tech text-xs uppercase tracking-wider text-slate-500 mb-2">{r.resetConfirm}</label>
                <input type="password" minLength={8} required autoComplete="new-password" disabled={!ready}
                  value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="••••••••"
                  className="w-full rounded-md border border-slate-300 px-4 py-3 text-[#0F172A] outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition disabled:bg-slate-50 disabled:text-slate-400" data-testid="reset-confirm-input" />
              </div>
              {error && <p className="text-sm text-red-600" data-testid="reset-error">{error}</p>}
              <button type="submit" disabled={!ready || saving} className="w-full rounded-full bg-emerald-500 py-3.5 font-semibold text-[#04170F] hover:bg-emerald-400 transition-colors disabled:opacity-60" data-testid="reset-submit">
                {saving ? "…" : r.resetCta}
              </button>
            </form>
          )}

          <Link to="/login" className="mt-8 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#0F172A] transition-colors" data-testid="reset-back">
            <ArrowLeft size={16} /> {r.backToLogin}
          </Link>
        </motion.div>
      </main>
    </div>
  );
}
